import { colorCharts } from "data/palettes";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./generic.module.scss";
import { formatItalianNumber } from "utils/formatUtils";

const renderCustomBarLabel = (props) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { x, y, width, height, value, index } = props;

  const textContent = `${formatItalianNumber(value, 2)}%`;
  const textWidth = textContent.length * 8;
  const padding = 0;
  const rectWidth = textWidth + padding * 2;

  const backgroundColor =
    index % 2 === 0
      ? colorsConfig.productCardBarChartLabel2
      : colorsConfig.productCardBarChartLabel1;

  return (
    <g>
      <rect
        x={x + width + 7}
        y={y + height / 2 - 12}
        width={rectWidth}
        height={23}
        fill={backgroundColor}
        rx={5}
      />
      <text
        x={x + width + 12}
        y={y + height / 2 + 0}
        fill={colorsConfig.FinecoBlack}
        dominantBaseline="middle"
        textAnchor="start"
        fontSize={12}
        fontWeight={700}
        fontFamily="Arial"
      >
        {`${formatItalianNumber(value, 2)}%`}
      </text>
    </g>
  );
};

const CustomYAxisTick = (props) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { x, y, payload } = props;

  const truncateText = (text, maxLength = 28) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={-10}
        y={0}
        dy={4}
        textAnchor="end"
        fill={colorsConfig.FinecoBlack}
        fontSize={14}
        fontFamily="Arial"
        fontWeight={400}
        className="yaxis-label"
      >
        {truncateText(payload.value)}
      </text>
    </g>
  );
};

const CustomXAxisTick = (props) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { x, y, payload } = props;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill={colorsConfig.FinecoBlack}
        fontSize={12}
        fontFamily="Arial"
        fontWeight={400}
        className="xaxis-label"
      >
        {`${payload.value}%`}
      </text>
    </g>
  );
};

// Improved tick calculation that handles all-zero datasets and negative values
const calculateNiceTicks = (minValue, maxValue) => {
  // If all values are zero, create a default scale
  if (minValue === 0 && maxValue === 0) {
    return [0, 25, 50, 75, 100];
  }

  // Round down min to the previous 10 and round up max to the next 10
  const roundedMin = Math.floor(minValue / 10) * 10;
  const roundedMax = Math.ceil(maxValue / 10) * 10;

  // Always include 0 in the range
  const effectiveMin = minValue < 0 ? roundedMin : 0;
  const effectiveMax = maxValue > 0 ? Math.max(roundedMax, 10) : 0;

  // If we have both negative and positive values, 0 will be in the middle
  // Otherwise, 0 will be at one end
  const range = effectiveMax - effectiveMin;

  // For a range that includes 0, we want to ensure 0 is always a tick
  if (effectiveMin < 0 && effectiveMax > 0) {
    // Calculate ticks on both sides of 0
    const negativeStep = Math.ceil(Math.abs(effectiveMin) / 2 / 10) * 10;
    const positiveStep = Math.ceil(effectiveMax / 2 / 10) * 10;

    return [
      -negativeStep * 2,
      -negativeStep,
      0,
      positiveStep,
      positiveStep * 2
    ];
  } else {
    // All positive or all negative - distribute 5 ticks evenly
    const step = range / 4;
    return Array.from({ length: 5 }, (_, i) => {
      const tick = effectiveMin + i * step;
      return Math.round(tick / 10) * 10;
    });
  }
};

export const GenericChart = ({ data, title }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const weights = data.map((item) => item.weight);
  const minWeight = Math.min(...weights);
  const maxWeight = Math.max(...weights);
  const ticks = calculateNiceTicks(minWeight, maxWeight);

  const formattedData = data.map((item, index) => ({
    ...item,
    weight: parseFloat(item.weight.toFixed(2)),
    fill: colorsConfig[colorCharts[index % colorCharts.length]],
  }));

  const chartHeight = formattedData.length * 40 + 54;

  return (
    <div>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={chartHeight} debounce={500}>
          <BarChart
            data={formattedData}
            layout="vertical"
            margin={{ top: 15, right: 80, left: 120, bottom: 15 }}
            barGap={30}
          >
            <CartesianGrid horizontal={false} vertical={false} />
            <XAxis
              type="number"
              domain={[ticks[0], ticks[ticks.length - 1]]}
              ticks={ticks}
              tick={<CustomXAxisTick />}
              tickLine={false}
              axisLine={{ stroke: colorsConfig.Gray3 }}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={100}
              interval={0}
              tick={<CustomYAxisTick />}
              axisLine={{ stroke: colorsConfig.Gray3 }}
              tickLine={false}
            />
            <Bar
              dataKey="weight"
              radius={[0, 4, 4, 0]}
              barSize={26}
              minPointSize={2}
              isAnimationActive={false}
            >
              <LabelList
                dataKey="weight"
                position="right"
                content={renderCustomBarLabel}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const GenericContainer = ({ data, title }) => {
  return (
    <div className={styles.container}>
      <GenericChart data={data} title={title} />
    </div>
  );
};

export default GenericContainer;
